import {
  DirectedTriple,
  DirectedTripleWithFilters,
  DirectedTripleWithFiltersWithGraphIds,
  PARENT_REF_TYPE_NAME,
} from '@ardoq/api-types';
import { MetamodelViewGraphEdge, MetaModelViewGraphNode } from '@ardoq/graph';
import {
  GraphId,
  SelectedGraphModelMap,
  TraversalBuilderFiltersPerNode,
} from '../types';

export type GetPathsArgs = {
  graphNodeId: string;
  sourceMap: SelectedGraphModelMap;
  targetMap: SelectedGraphModelMap;
  graphNodes: Map<string, MetaModelViewGraphNode>;
  graphEdges: Map<string, MetamodelViewGraphEdge>;
  path: DirectedTriple[];
  visited: Set<string>;
  filters: Record<GraphId, TraversalBuilderFiltersPerNode>;
  /**
   * If `targetNodeId` is provided, the traversal will stop when the target node
   * is reached and only return the path to that node.
   */
  targetNodeId: string | null;
  requiredNodeIds: string[];
  includeGraphIds: boolean;
};

export const getPaths = (args: GetPathsArgs): DirectedTripleWithFilters[][] => {
  const {
    graphNodeId,
    sourceMap,
    targetMap,
    graphNodes,
    graphEdges,
    path,
    visited,
    filters,
    targetNodeId,
    requiredNodeIds,
    includeGraphIds,
  } = args;
  const targets = (sourceMap.get(graphNodeId) ?? []).filter(
    ({ componentId }) => !visited.has(componentId)
  );
  const sources = (targetMap.get(graphNodeId) ?? []).filter(
    ({ componentId }) => !visited.has(componentId)
  );

  const linkedComponents = [...targets, ...sources];
  const linkedTargetNode =
    targetNodeId &&
    linkedComponents.find(({ componentId }) => componentId === targetNodeId);
  if (linkedTargetNode) {
    const direction = targets.find(
      ({ componentId }) => componentId === targetNodeId
    )
      ? 'outgoing'
      : 'incoming';
    const isOutgoing = direction === 'outgoing';
    return [
      [
        ...path,
        toDirectedTripleWithFilters({
          sourceId: isOutgoing ? graphNodeId : targetNodeId,
          targetId: isOutgoing ? targetNodeId : graphNodeId,
          referenceId: linkedTargetNode.referenceId,
          direction,
          graphNodes,
          graphEdges,
          filters,
          isRequired: requiredNodeIds.includes(
            isOutgoing ? targetNodeId : graphNodeId
          ),
          includeGraphIds,
        }),
      ],
    ];
  }

  [...targets, ...sources].forEach(({ componentId }) =>
    visited.add(componentId)
  );

  if (sources.length === 0 && targets.length === 0) {
    // If the `targetNodeId` is provided return an empty path for all other leaf
    // nodes.
    return targetNodeId ? [] : [path];
  }

  return [
    ...targets.flatMap(({ referenceId, componentId }) =>
      getPaths({
        ...args,
        graphNodeId: componentId,
        path: [
          ...path,
          toDirectedTripleWithFilters({
            sourceId: graphNodeId,
            targetId: componentId,
            referenceId,
            direction: 'outgoing',
            graphNodes,
            graphEdges,
            filters,
            isRequired: requiredNodeIds.includes(componentId),
            includeGraphIds,
          }),
        ],
      })
    ),
    ...sources.flatMap(({ referenceId, componentId }) =>
      getPaths({
        ...args,
        graphNodeId: componentId,
        path: [
          ...path,
          toDirectedTripleWithFilters({
            sourceId: componentId,
            targetId: graphNodeId,
            referenceId,
            direction: 'incoming',
            graphNodes,
            graphEdges,
            filters,
            isRequired: requiredNodeIds.includes(componentId),
            includeGraphIds,
          }),
        ],
      })
    ),
  ];
};

type ToDirectedTriplesArgs = {
  sourceId: string;
  targetId: string;
  referenceId: string;
  direction: 'outgoing' | 'incoming';
  graphNodes: Map<string, MetaModelViewGraphNode>;
  graphEdges: Map<string, MetamodelViewGraphEdge>;
  filters: Record<GraphId, TraversalBuilderFiltersPerNode>;
  isRequired: boolean;
  includeGraphIds: boolean;
};

const toDirectedTripleWithFilters = ({
  sourceId,
  targetId,
  referenceId,
  direction,
  graphNodes,
  graphEdges,
  filters,
  isRequired,
  includeGraphIds,
}: ToDirectedTriplesArgs):
  | DirectedTripleWithFilters
  | DirectedTripleWithFiltersWithGraphIds => {
  const directedTripleWithFilters: DirectedTripleWithFilters = {
    sourceType: getComponentTypeLabel(sourceId, graphNodes),
    targetType: getComponentTypeLabel(targetId, graphNodes),
    referenceType: getReferenceTypeLabel(referenceId, graphEdges),
    direction,
    qualifier: isRequired ? 'required' : undefined,
    sourceFilter: filters[sourceId]?.localFilterId ?? undefined,
    targetFilter: filters[targetId]?.localFilterId ?? undefined,
    referenceFilter: filters[referenceId]?.localFilterId ?? undefined,
  };

  if (!includeGraphIds) return directedTripleWithFilters;

  return {
    ...directedTripleWithFilters,
    sourceGraphId: sourceId,
    targetGraphId: targetId,
    referenceGraphId: referenceId,
  };
};

export const getComponentTypeLabel = (
  id: string,
  graphNodes: Map<string, MetaModelViewGraphNode>
) => {
  const node = graphNodes.get(id);
  return node?.metaData?.label ?? '';
};

const getReferenceTypeLabel = (
  id: string,
  graphEdges: Map<string, MetamodelViewGraphEdge>
) => {
  const edge = graphEdges.get(id);
  // We need to get the raw label, not necessarily the one displayed in the UI.
  const label = edge?.getRepresentationData()?.displayLabel ?? '';
  return toBELabel(label);
};

export const toBELabel = (label: string) =>
  label === PARENT_REF_TYPE_NAME ? 'ardoq_parent' : label;
